import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  DollarSign, 
  Users, 
  Calendar,
  FileText,
  MessageCircle,
  TrendingUp,
  Star,
  Settings,
  BarChart3,
  Receipt,
  BookOpen,
  Mail,
  Edit,
  Video,
  ChevronDown,
  ChevronRight,
  User,
  Smartphone
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { usePlatform } from '../../contexts/PlatformContext';
import { useAuth } from '../../contexts/AuthContext';
import PlatformToggle from '../layout/PlatformToggle';
import BusinessDashboard from './BusinessDashboard';
import TrainingOpportunities from './TrainingOpportunities';
import MyBookings from './MyBookings';
import ProposalsContracts from './ProposalsContracts';
import BillingInvoices from './BillingInvoices';
import ClientMessages from './ClientMessages';
import RevenueAnalytics from './RevenueAnalytics';
import ClientReviews from './ClientReviews';
import BusinessSettings from './BusinessSettings';
import MyMaterials from './MyMaterials';
import ProposalTemplates from './ProposalTemplates';
import ClientDirectory from './ClientDirectory';
import UserTypeSelection from '../auth/UserTypeSelection';
import OrganizationBusinessHub from './OrganizationBusinessHub';
import DigiTPro from '../../pages/DigiTPro';
import ProposalDetails from '../../pages/ProposalDetails';
import MessageCompose from '../../pages/MessageCompose';
import TrainingFeedback from './TrainingFeedback';

const BusinessHub = () => {
  const { toggleMode } = usePlatform();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [userType, setUserType] = useState(null);
  const [showUserTypeSelection, setShowUserTypeSelection] = useState(false);

  // Determine active section from current route
  const getActiveSectionFromPath = () => {
    const path = location.pathname;
    
    // Handle business hub routes
    if (path === '/business' || path === '/business/') return 'dashboard';
    if (path.startsWith('/business/opportunities')) return 'opportunities';
    if (path.startsWith('/business/bookings')) return 'bookings';
    if (path.startsWith('/business/proposals')) return 'proposals';
    if (path.startsWith('/business/proposal-templates')) return 'proposal-templates';
    if (path.startsWith('/business/billing')) return 'billing';
    if (path.startsWith('/business/messages')) return 'messages';
    if (path.startsWith('/business/directory')) return 'directory';
    if (path.startsWith('/business/materials')) {
      const parts = path.split('/');
      return parts[3] || 'materials'; // Get material type or default to materials
    }
    if (path.startsWith('/business/revenue')) return 'revenue';
    if (path.startsWith('/business/reviews')) return 'reviews';
    if (path.startsWith('/business/settings')) return 'settings';
    
    // Handle shared routes that work in business context
    if (path.startsWith('/digitpro')) return location.pathname;
    if (path.startsWith('/proposal/')) return location.pathname;
    if (path.startsWith('/messages/compose')) return location.pathname;
    
    return 'dashboard';
  };

  const activeSection = getActiveSectionFromPath();

  // Auto-expand materials menu if on a materials sub-page
  useEffect(() => {
    if (activeSection.includes('materials') || 
        ['class-materials', 'reading-materials', 'quizzes', 'ice-breakers', 'videos'].includes(activeSection)) {
      setExpandedMenus(prev => ({ ...prev, materials: true }));
    }
  }, [activeSection]);

  // Check for user type on component mount
  useEffect(() => {
    // Use actual user type from database
    if (user?.user_type) {
      setUserType(user.user_type);
      localStorage.setItem('userType', user.user_type);
    } else {
      // Fallback to localStorage if user type not in auth context
      const savedUserType = localStorage.getItem('userType');
      if (savedUserType) {
        setUserType(savedUserType);
      } else {
        setShowUserTypeSelection(true);
      }
    }
  }, [user]);

  const handleUserTypeSelected = (selectedType) => {
    setUserType(selectedType);
    localStorage.setItem('userType', selectedType);
    setShowUserTypeSelection(false);
  };

  // Show user type selection if not set
  if (showUserTypeSelection) {
    return <UserTypeSelection onUserTypeSelected={handleUserTypeSelected} />;
  }

  // Render Organization Business Hub for organization users
  if (userType === 'organization') {
    return <OrganizationBusinessHub />;
  }

  // Default to Trainer Business Hub (existing functionality)

  // Mock business data
  const businessData = {
    monthlyRevenue: 4200,
    activeClients: 12,
    notifications: {
      trainingOpportunities: 8,
      bookings: 4,
      billing: 2,
      messages: 3
    }
  };

  // Calculate total business notifications for the toggle badge
  const businessNotifications = Object.values(businessData.notifications).reduce((sum, count) => sum + count, 0);

  const navigationItems = [
    { 
      id: 'dashboard', 
      path: '/business',
      label: 'Dashboard', 
      icon: BarChart3, 
      badge: null 
    },
    { 
      id: 'opportunities', 
      path: '/business/opportunities',
      label: 'Training Opportunities', 
      icon: BookOpen, 
      badge: businessData.notifications.trainingOpportunities 
    },
    { 
      id: 'bookings', 
      path: '/business/bookings',
      label: 'My Bookings', 
      icon: Calendar, 
      badge: businessData.notifications.bookings 
    },
    { 
      id: 'proposals', 
      path: '/business/proposals',
      label: 'Proposals & Contracts', 
      icon: FileText, 
      badge: null 
    },
    { 
      id: 'billing', 
      path: '/business/billing',
      label: 'Billing & Invoices', 
      icon: Receipt, 
      badge: businessData.notifications.billing 
    },
    { 
      id: 'messages', 
      path: '/business/messages',
      label: 'Client Messages', 
      icon: MessageCircle, 
      badge: businessData.notifications.messages 
    },
    { 
      id: 'directory', 
      path: '/business/directory',
      label: 'Client & Participant Directory', 
      icon: Users, 
      badge: null 
    },
    { 
      id: 'training-feedback', 
      path: '/business/training-feedback',
      label: 'Training Feedback', 
      icon: Smartphone, 
      badge: null 
    },
    { 
      id: 'materials', 
      path: '/business/materials',
      label: 'My Materials', 
      icon: BookOpen, 
      badge: null,
      submenu: [
        { id: 'class-materials', path: '/business/materials/class-materials', label: 'Class Materials', icon: BookOpen },
        { id: 'reading-materials', path: '/business/materials/reading-materials', label: 'Reading Materials', icon: FileText },
        { id: 'quizzes', path: '/business/materials/quizzes', label: 'Quizzes', icon: Edit },
        { id: 'ice-breakers', path: '/business/materials/ice-breakers', label: 'Ice Breakers', icon: Users },
        { id: 'videos', path: '/business/materials/videos', label: 'Videos', icon: Video }
      ]
    }
  ];

  const analyticsItems = [
    { 
      id: 'revenue', 
      path: '/business/revenue',
      label: 'Revenue Analytics', 
      icon: TrendingUp 
    },
    { 
      id: 'reviews', 
      path: '/business/reviews',
      label: 'Client Reviews', 
      icon: Star 
    }
  ];

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Business Hub Header - Enhanced with Toggle */}
      <div className="bg-gradient-to-r from-green-700 via-green-600 to-green-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-4">
                <Avatar className="w-16 h-16 border-4 border-white shadow-lg">
                  <AvatarImage src={user?.avatar} alt={user?.name} />
                  <AvatarFallback className="bg-green-600 text-white text-xl font-bold">
                    <User className="w-8 h-8" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h1 className="text-2xl font-bold">Business Hub</h1>
                  <div className="flex items-center space-x-4 mt-1">
                    <p className="text-green-100 font-medium">{user?.name}</p>
                    <p className="text-green-200 text-sm">Professional Training Dashboard</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-8">
                <div className="text-center">
                  <div className="flex items-center text-2xl font-bold">
                    <DollarSign className="w-6 h-6 mr-1" />
                    ${businessData.monthlyRevenue.toLocaleString()}
                  </div>
                  <p className="text-green-200 text-sm">This month</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center text-2xl font-bold">
                    <Users className="w-6 h-6 mr-1" />
                    {businessData.activeClients}
                  </div>
                  <p className="text-green-200 text-sm">Active clients</p>
                </div>
              </div>
            </div>
            
            {/* Enhanced Platform Toggle in Business Hub */}
            <div className="flex items-center space-x-4">
              <PlatformToggle 
                currentMode="business"
                onToggle={toggleMode}
                businessNotifications={businessNotifications}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Business Hub Layout */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Business Navigation Sidebar - Redesigned */}
          <div className="col-span-3">
            <Card className="sticky top-6 shadow-soft">
              <CardContent className="p-4">
                {/* Primary Navigation */}
                <div className="space-y-1 mb-6">
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    const hasSubmenu = item.submenu && item.submenu.length > 0;
                    const isExpanded = expandedMenus[item.id];
                    
                    return (
                      <div key={item.id}>
                        <div
                          onClick={() => {
                            if (hasSubmenu) {
                              toggleMenu(item.id);
                              if (!isExpanded) {
                                navigate(item.path);
                              }
                            } else {
                              navigate(item.path);
                            }
                          }}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-sm ${
                            activeSection === item.id || activeSection.startsWith(item.id)
                              ? 'bg-green-50 text-green-800 border-l-4 border-green-600 shadow-sm'
                              : 'hover:bg-gray-50 text-gray-700 hover:text-green-700'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <Icon className="w-5 h-5" />
                            <span className="font-medium text-sm">{item.label}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {item.badge && (
                              <Badge variant="destructive" className="bg-red-500 text-white text-xs px-2">
                                {item.badge}
                              </Badge>
                            )}
                            {hasSubmenu && (
                              <div className="transition-transform duration-200">
                                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Submenu */}
                        {hasSubmenu && isExpanded && (
                          <div className="ml-6 mt-1 space-y-1">
                            {item.submenu.map((subItem) => {
                              const SubIcon = subItem.icon;
                              return (
                                <div
                                  key={subItem.id}
                                  onClick={() => navigate(subItem.path)}
                                  className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-all duration-200 ${
                                    activeSection === subItem.id
                                      ? 'bg-green-100 text-green-800'
                                      : 'hover:bg-gray-50 text-gray-600 hover:text-green-700'
                                  }`}
                                >
                                  <SubIcon className="w-4 h-4" />
                                  <span className="text-sm font-medium">{subItem.label}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Add Proposal Templates to Proposals section */}
                  <div
                    onClick={() => navigate('/business/proposal-templates')}
                    className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ml-6 ${
                      activeSection === 'proposal-templates'
                        ? 'bg-green-100 text-green-800'
                        : 'hover:bg-gray-50 text-gray-600 hover:text-green-700'
                    }`}
                  >
                    <Edit className="w-4 h-4" />
                    <span className="text-sm font-medium">Proposal Templates</span>
                  </div>
                </div>

                {/* Reports & Analytics Section */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-800 mb-3 text-xs uppercase tracking-wide px-3">
                    Reports & Analytics
                  </h3>
                  <div className="space-y-1">
                    {analyticsItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={item.id}
                          onClick={() => navigate(item.path)}
                          className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                            activeSection === item.id
                              ? 'bg-green-50 text-green-800 border-l-4 border-green-600'
                              : 'hover:bg-gray-50 text-gray-700 hover:text-green-700'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="font-medium text-sm">{item.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Business Settings */}
                <div
                  onClick={() => navigate('/business/settings')}
                  className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                    activeSection === 'settings'
                      ? 'bg-green-50 text-green-800 border-l-4 border-green-600'
                      : 'hover:bg-gray-50 text-gray-700 hover:text-green-700'
                  }`}
                >
                  <Settings className="w-5 h-5" />
                  <span className="font-medium text-sm">Business Settings</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Business Content Area */}
          <div className="col-span-9">
            <Routes>
              {/* Default/Dashboard Route */}
              <Route index element={<Navigate to="/business" replace />} />
              <Route path="/" element={<BusinessDashboard businessData={businessData} />} />
              <Route path="/business" element={<BusinessDashboard businessData={businessData} />} />
              
              {/* Business Hub Routes */}
              <Route path="/business/opportunities" element={<TrainingOpportunities />} />
              <Route path="/business/bookings" element={<MyBookings />} />
              <Route path="/business/proposals" element={<ProposalsContracts />} />
              <Route path="/business/proposal-templates" element={<ProposalTemplates />} />
              <Route path="/business/billing" element={<BillingInvoices />} />
              <Route path="/business/messages" element={<ClientMessages />} />
              <Route path="/business/directory" element={<ClientDirectory />} />
              
              {/* Materials Routes */}
              <Route path="/business/materials" element={<MyMaterials activeTab="materials" />} />
              <Route path="/business/materials/class-materials" element={<MyMaterials activeTab="class-materials" />} />
              <Route path="/business/materials/reading-materials" element={<MyMaterials activeTab="reading-materials" />} />
              <Route path="/business/materials/quizzes" element={<MyMaterials activeTab="quizzes" />} />
              <Route path="/business/materials/ice-breakers" element={<MyMaterials activeTab="ice-breakers" />} />
              <Route path="/business/materials/videos" element={<MyMaterials activeTab="videos" />} />
              
              {/* Training Feedback Route */}
              <Route path="/business/training-feedback" element={<TrainingFeedback />} />
              
              {/* Analytics Routes */}
              <Route path="/business/revenue" element={<RevenueAnalytics />} />
              <Route path="/business/reviews" element={<ClientReviews />} />
              
              {/* Settings Route */}
              <Route path="/business/settings" element={<BusinessSettings />} />
              
              {/* Shared Routes (work in both social and business) */}
              <Route path="/digitpro" element={<DigiTPro />} />
              <Route path="/digitpro/:trainerId" element={<DigiTPro />} />
              <Route path="/proposal/:proposalId" element={<ProposalDetails />} />
              <Route path="/messages/compose" element={<MessageCompose />} />
              
              {/* Fallback to Dashboard */}
              <Route path="*" element={<Navigate to="/business" replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessHub;